import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CustomTableHead, CustomTableHeadCell } from '../../../components/@extended/CustomTable';
import Edit from '@mui/icons-material/Edit';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import CustomButton from '../../../components/@extended/CustomButton';
import moment from 'moment';
import {
  useAmendInvoiceMutation,
  useGenerateFianceInvoiceMutation,
  useGetInvoiceQuery,
  useGetPendingInvoiceAmendmentsQuery,
  useLazyGetInvoiceQuery,
} from '../../../store/slices/apiSlice';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { calculateInvoiceAmounts } from './invoiceFeeUtils';
import ReadonlyApprovalDetails from './ReadonlyApprovalDetails';

const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;
  const data = error?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  if (error?.message) return error.message;
  return fallback;
};

const validationSchema = Yup.object().shape({
  amendmentType: Yup.string().required('Amendment type is required'),
  reason: Yup.string(),
});

const isAmendmentLineItem = (description = '') =>
  String(description).startsWith('Amendment (');

const formatAmendmentType = (type = '') => String(type).replace(/_/g, ' ');

const CHANGE_TYPE_LABELS = {
  added: 'New',
  updated: 'Updated',
  removed: 'Removed',
};

const computeAmendmentRawAmount = (amendment) => {
  const fromApi = Number(amendment?.calculatedDifference);
  if (fromApi) return fromApi;

  const items = amendment?.details?.amendmentItems ?? [];
  if (items.length === 0) {
    return Number(amendment?.amountDifference) || 0;
  }

  return items.reduce((sum, item) => {
    if (!item.id) {
      return sum + (Number(item.amount) || 0) * (Number(item.quantity) || 0);
    }
    return sum;
  }, 0);
};

const buildAmendmentItemBreakdownFallback = (amendment) => {
  const items = amendment?.itemBreakdown;
  if (Array.isArray(items) && items.length > 0) return items;

  return (amendment?.details?.amendmentItems ?? []).map((item, index) => {
    const amount = Number(item.amount) || 0;
    const quantity = Number(item.quantity) || 0;
    const lineTotal = amount * quantity;
    return {
      changeType: item.id ? 'updated' : 'added',
      itemId: item.id ?? null,
      description: item.description,
      amount,
      quantity,
      lineTotal,
      adjustmentAmount: lineTotal,
      rowKey: `${amendment?.id ?? 'amendment'}-fallback-${index}`,
    };
  });
};

const buildAmendmentDisplayRows = (amendments, { badge, prefix = 'A', includeInvoiceRef = true } = {}) =>
  amendments.flatMap((amendment, index) => {
    const typeLabel = formatAmendmentType(amendment.type);
    const invoiceRef =
      amendment.originalInvoiceNumber || amendment.originalInvoiceId || 'N/A';
    const headerDescription = includeInvoiceRef
      ? `Amendment (${typeLabel}) — Invoice ${invoiceRef}: ${amendment.reason}`
      : `Amendment (${typeLabel}): ${amendment.reason}`;
    const adjustmentTotal = computeAmendmentRawAmount(amendment);
    const itemBreakdown = buildAmendmentItemBreakdownFallback(amendment);

    const headerRow = {
      rowKey: `${amendment.id}-header`,
      isAmendmentHeader: true,
      srno: `${prefix}${index + 1}`,
      description: headerDescription,
      amount: null,
      quantity: null,
      total: adjustmentTotal,
      badge,
      createdAt: amendment.createdAt,
    };

    const itemRows = itemBreakdown.map((item, itemIndex) => ({
      rowKey: `${amendment.id}-item-${item.itemId ?? itemIndex}`,
      isAmendmentItem: true,
      srno: '',
      description: item.description,
      changeType: item.changeType,
      previousAmount: item.previousAmount,
      previousQuantity: item.previousQuantity,
      amount: item.amount,
      quantity: item.quantity,
      total: item.adjustmentAmount ?? item.lineTotal,
      lineTotal: item.lineTotal,
      badge,
    }));

    return [headerRow, ...itemRows];
  });

const GenerateInvoice = ({
  handleClose,
  isUpdate = false,
  isEditable = false,
  invoiceId,
  invoiceStatus: invoiceStatusProp,
  trialId,
  trialSiteId,
}) => {
  const [generateFianceInvoice, { isLoading }] = useGenerateFianceInvoiceMutation();
  const [amendInvoice, { isLoading: amendLoading }] = useAmendInvoiceMutation();
  const isSubmitting = isLoading || amendLoading;
  const actor = useSelector((state) => state?.userDetails?.loggedInUser?.email);

  const columns = useMemo(
    () => [
      { key: 'srno', label: 'Sr. No.', maxWidth: 80, align: 'center' },
      { key: 'description', label: 'Service Description', minWidth: 180, align: 'center' },
      { key: 'amount', label: 'Amount', minWidth: 80, align: 'center' },
      { key: 'quantity', label: 'UoQ', minWidth: 80, align: 'center' },
      { key: 'total', label: 'Total', minWidth: 80, align: 'center' },
      ...(isEditable ? [{ key: 'actions', label: '', minWidth: 140, align: 'center' }] : []),
    ],
    [isEditable]
  );

  const dispatch = useDispatch();

  const [getInvoice, { data: invoiceData }] = useLazyGetInvoiceQuery();

  useEffect(() => {
    // console.log('invoiceId', invoiceId);
    if (invoiceId) {
      getInvoice({ id: invoiceId });
    }
  }, [invoiceId]);

  const theme = useTheme();

  const selectedSite = useSelector((state) => state.finance?.currentSite || {});
  const sponsor = useSelector((state) => state.userDetails?.user || {});
  const recipientEntityId = sponsor?.financeDetail?.entityId;

  const { data: pendingAmendmentsData } = useGetPendingInvoiceAmendmentsQuery(
    { recipientEntityId },
    { skip: isUpdate || !recipientEntityId }
  );

  const pendingAmendments = pendingAmendmentsData?.amendments ?? [];

  const pendingAmendmentRows = useMemo(
    () =>
      buildAmendmentDisplayRows(pendingAmendments, {
        badge: 'Pending',
        prefix: 'A',
        includeInvoiceRef: true,
      }),
    [pendingAmendmentsData]
  );

  const pendingAmendmentTotal = useMemo(
    () => pendingAmendments.reduce((sum, amendment) => sum + computeAmendmentRawAmount(amendment), 0),
    [pendingAmendmentsData]
  );

  const appliedAmendments = invoiceData?.amendmentInfo?.appliedAmendments ?? [];

  const appliedAmendmentRows = useMemo(() => {
    if (!isUpdate || appliedAmendments.length === 0) return [];
    return buildAmendmentDisplayRows(appliedAmendments, {
      badge: 'Applied',
      prefix: 'P',
      includeInvoiceRef: true,
    });
  }, [invoiceData, isUpdate]);

  const appliedAmendmentTotal = useMemo(
    () => appliedAmendments.reduce((sum, amendment) => sum + computeAmendmentRawAmount(amendment), 0),
    [invoiceData]
  );

  const recordedAmendments = invoiceData?.amendmentInfo?.amendments ?? [];

  const recordedAmendmentRows = useMemo(() => {
    if (!isUpdate) return [];
    return buildAmendmentDisplayRows(recordedAmendments, {
      badge: 'For next invoice',
      prefix: 'R',
      includeInvoiceRef: false,
    });
  }, [invoiceData, isUpdate]);

  const formatAdjustmentTotal = (value) => {
    const num = Number(value) || 0;
    if (num > 0) return `+$${num.toFixed(2)}`;
    if (num < 0) return `-$${Math.abs(num).toFixed(2)}`;
    return '$0.00';
  };

  const renderAmendmentDescription = (row) => {
    if (row.isAmendmentHeader) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.description}
        </Typography>
      );
    }

    if (row.isAmendmentItem) {
      const changeLabel = CHANGE_TYPE_LABELS[row.changeType] || row.changeType;
      const previousDetail =
        row.changeType === 'updated' &&
        row.previousAmount != null &&
        row.previousQuantity != null
          ? ` (was $${Number(row.previousAmount).toFixed(2)} × ${row.previousQuantity})`
          : '';

      return (
        <Box sx={{ pl: 2 }}>
          <Typography variant="body2" component="span">
            {row.description}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 600,
              color:
                row.changeType === 'removed'
                  ? 'error.main'
                  : row.changeType === 'added'
                    ? 'success.main'
                    : 'info.main',
            }}
          >
            {changeLabel}
          </Typography>
          {previousDetail && (
            <Typography variant="caption" color="text.secondary" display="block">
              {previousDetail.trim()}
            </Typography>
          )}
        </Box>
      );
    }

    return row.description;
  };

  const renderAmendmentTableRows = (rows, { includeActions = true, showStatusColumn = false } = {}) => {
    const displayColumns = includeActions
      ? columns
      : columns.filter((col) => col.key !== 'actions');

    return rows.map((row) => (
      <TableRow
        key={row.rowKey}
        sx={{
          backgroundColor: row.isAmendmentHeader
            ? theme.palette.action.selected
            : theme.palette.action.hover,
        }}
      >
        {displayColumns.map((column) => {
          if (column.key === 'actions') {
            return (
              <TableCell align="center" key={column.key} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                {!row.isAmendmentItem && (
                  <Typography variant="caption" color="text.secondary">
                    {row.badge}
                  </Typography>
                )}
              </TableCell>
            );
          }
          if (column.key === 'srno') {
            return (
              <TableCell align="center" key={column.key} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                {row.srno}
              </TableCell>
            );
          }
          if (column.key === 'description') {
            return (
              <TableCell key={column.key} align={column.align} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                {renderAmendmentDescription(row)}
              </TableCell>
            );
          }
          if (column.key === 'amount') {
            const value = row.amount;
            return (
              <TableCell key={column.key} align={column.align} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                {value == null ? '—' : `$${Number(value).toFixed(2)}`}
              </TableCell>
            );
          }
          if (column.key === 'quantity') {
            return (
              <TableCell key={column.key} align={column.align} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                {row.quantity == null ? '—' : row.quantity}
              </TableCell>
            );
          }
          if (column.key === 'total') {
            return (
              <TableCell key={column.key} align={column.align} sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: row.isAmendmentHeader ? 600 : 400 }}
                >
                  {formatAdjustmentTotal(row.total)}
                </Typography>
              </TableCell>
            );
          }
          return (
            <TableCell key={column.key} align={column.align} sx={{ py: 1 }}>
              {row[column.key]}
            </TableCell>
          );
        })}
        {showStatusColumn && (
          <TableCell align="center" sx={{ py: row.isAmendmentItem ? 0.75 : 1 }}>
            {!row.isAmendmentItem && (
              <Typography variant="caption" color="text.secondary">
                {row.badge}
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
    ));
  };

  const currentInvoiceStatus = invoiceData?.invoiceDetails?.status || invoiceStatusProp;
  const isApprovedOrAmended =
    currentInvoiceStatus === 'APPROVED' || currentInvoiceStatus === 'AMENDED';
  const isPendingInvoice = currentInvoiceStatus === 'PENDING';
  const isTerminalInvoice =
    currentInvoiceStatus === 'APPROVED' ||
    currentInvoiceStatus === 'DECLINED' ||
    currentInvoiceStatus === 'AMENDED';

  const siteInfo = useMemo(() => {
    if (!selectedSite?.siteEntity?.addditionalInfo) return {};
    try {
      return JSON.parse(selectedSite?.siteEntity?.addditionalInfo);
    } catch (error) {
      console.error('Error parsing site additional info:', error);
      return {};
    }
  }, [selectedSite]);

  // Initialize with default data or empty array
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [formData, setFormData] = useState({ description: '', amount: '', quantity: '', total: '' });

  // Initialize with either passed data or default values
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(moment().format('DD MMM YYYY'));

  const [deletedRows, setDeletedRows] = useState([]);

  // Load invoice data if provided in props
  useEffect(() => {
    try {
      if (invoiceData) {
        // Set invoice items from props data
        if (invoiceData.items && Array.isArray(invoiceData.items) && invoiceData.items.length > 0) {
          const regularItems = invoiceData.items.filter(
            (item) => !isAmendmentLineItem(item.description)
          );
          const formattedItems = regularItems.map((item, index) => ({
            srno: index + 1,
            description: item.description || '',
            amount: Number(item.amount) || 0,
            quantity: Number(item.quantity) || 0,
            total: (Number(item.amount) || 0) * (Number(item.quantity) || 0),
            id: item?.id,
          }));
          setInvoiceItems(
            formattedItems.length > 0
              ? formattedItems
              : [{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]
          );
        } else {
          // Set default empty item if no items provided
          setInvoiceItems([{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]);
        }

        // Set invoice number and date if provided
        if (invoiceData?.invoiceDetails?.invoiceNumber) {
          setInvoiceNumber(invoiceData?.invoiceDetails?.invoiceNumber);
        } else {
          generateInvoiceNumber();
        }

        if (invoiceData?.invoiceDetails?.invoiceDate) {
          setInvoiceDate(moment(invoiceData.invoiceDetails.invoiceDate, 'DD-MM-YYYY').format('DD MMM YYYY'));
        }
      } else if (!isUpdate) {
        setInvoiceItems([{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]);
        generateInvoiceNumber();
      }
    } catch (error) {
      console.error('Error setting up invoice data:', error);
      // Set failsafe defaults
      setInvoiceItems([{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]);
      generateInvoiceNumber();
    }
  }, [invoiceData]);

  const generateInvoiceNumber = () => {
    const siteCode = selectedSite?.siteCode || 'INV';
    const datePart = moment().format('DDMMYYYYHHmm');
    const randomPart = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
    setInvoiceNumber(`${siteCode}-${datePart}-${randomPart}`);
  };

  const handleEdit = (index) => {
    if (!isEditable) return;

    setEditIndex(index);
    setFormData({ ...invoiceItems[index] });
  };

  const handleChange = (e, key) => {
    if (!isEditable) return;

    let value = e.target.value;

    // Validate numeric inputs
    if (key === 'amount' || key === 'quantity') {
      value = value === '' ? 0 : Number(value);
      if (isNaN(value) || value < 0) value = 0;
    }

    let updatedData = { ...formData, [key]: value };

    // Calculate total whenever amount or quantity changes
    if (key === 'amount' || key === 'quantity') {
      const amount = key === 'amount' ? value : Number(formData.amount) || 0;
      const quantity = key === 'quantity' ? value : Number(formData.quantity) || 0;
      updatedData.total = amount * quantity;
    }

    setFormData(updatedData);
  };

  const handleSave = () => {
    if (!isEditable || editIndex === null) return;

    // Validate form data before saving
    if (!formData.description || formData.description.trim() === '') {
      alert('Description cannot be empty');
      return;
    }

    const updatedItems = invoiceItems.map((item, index) =>
      index === editIndex
        ? {
            ...formData,
            description: formData.description.trim(),
            amount: Number(formData.amount) || 0,
            quantity: Number(formData.quantity) || 0,
            total: Number(formData.total) || 0,
          }
        : item
    );

    setInvoiceItems(updatedItems);
    setEditIndex(null);
  };

  const handleRemoveRow = (index) => {
    if (!isEditable) return;

    // Don't allow removing the last row
    if (invoiceItems.length <= 1) return;

    const item = invoiceItems[index];
    if (item?.id) {
      setDeletedRows([...deletedRows, item.id]);
    }
    const updatedItems = invoiceItems.filter((_, i) => i !== index).map((item, i) => ({ ...item, srno: i + 1 })); // Update sr no

    setInvoiceItems(updatedItems);

    // Reset edit mode if the edited row was removed
    if (editIndex === index) {
      setEditIndex(null);
    } else if (editIndex !== null && editIndex > index) {
      // Adjust editIndex if a row above it was removed
      setEditIndex(editIndex - 1);
    }
  };

  const handleAddRow = () => {
    if (!isEditable) return;

    // If currently editing, save current changes first
    if (editIndex !== null) {
      handleSave();
    }

    const newItem = {
      srno: invoiceItems.length + 1,
      description: '',
      amount: 0,
      quantity: 0,
      total: 0,
    };

    setInvoiceItems([...invoiceItems, newItem]);
    setEditIndex(invoiceItems.length);
    setFormData(newItem);
  };

  const { subtotal, taxes, stripeCharges, transactionHandlingCost, totalAmountDue } = useMemo(() => {
    try {
      if (isUpdate && isTerminalInvoice && invoiceData?.amounts) {
        return {
          subtotal: Number(invoiceData.amounts.subtotal) || 0,
          taxes: Number(invoiceData.amounts.taxes) || 0,
          stripeCharges: Number(invoiceData.amounts.stripeCharges) || 0,
          transactionHandlingCost: Number(invoiceData.amounts.transactionHandlingCost) || 0,
          totalAmountDue: Number(invoiceData.amounts.amount) || 0,
        };
      }

      const lineSubtotal = invoiceItems.reduce(
        (acc, item) => acc + Number(item.total || 0),
        0
      );
      const amendmentSubtotal = pendingAmendmentTotal + appliedAmendmentTotal;
      return calculateInvoiceAmounts(lineSubtotal + amendmentSubtotal);
    } catch (error) {
      console.error('Error calculating totals:', error);
      return {
        subtotal: 0,
        taxes: 0,
        stripeCharges: 0,
        transactionHandlingCost: 0,
        totalAmountDue: 0,
      };
    }
  }, [
    invoiceItems,
    pendingAmendmentTotal,
    appliedAmendmentTotal,
    isUpdate,
    isTerminalInvoice,
    invoiceData,
  ]);

  const validateInvoice = () => {
    // Check if any item is missing required fields
    const invalidItems = invoiceItems.some(
      (item) => !item.description || item.description.trim() === '' || !item.amount || !item.quantity
    );

    if (invalidItems) {
      return false;
    }

    return true;
  };

  const handleGenerateInvoice = async (values, validateForm) => {
    if (!isEditable) return;

    // Validate all fields before submitting
    if (!validateInvoice()) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: 'Please fill in all required fields for invoice items',
        })
      );

      return;
    }

    try {
      if (isUpdate) {
        if (isApprovedOrAmended) {
          const formErrors = await validateForm();

          // Check if there are any validation errors
          if (Object.keys(formErrors).length > 0) {
            // There are validation errors
            const errorMessages = Object.values(formErrors).join(', ');
            dispatch(
              openMessage({
                messageSeverity: 'error',
                message: `Please fix the following errors: ${errorMessages}`,
              })
            );
            return;
          }
        }
        const body = {
          reason: values.reason || 'Changing price',
          amendmentType: values.amendmentType|| 'additional_charge' ,
          items: invoiceItems.map((i) => {
            return {
              id: i?.id,
              description: i?.description,
              amount: Number(i?.amount),
              quantity: Number(i?.quantity),
            };
          }),
          itemsToRemove: deletedRows,
        };

        const res = await amendInvoice({ id: invoiceId, body, actor });
        if (res.data) {
          handleClose();
        } else if (res.error) {
          dispatch(
            openMessage({
              messageSeverity: 'error',
              message: getApiErrorMessage(res.error, 'Failed to update invoice.'),
            })
          );
        }
      } else {
        const body = {
          invoiceNumber: invoiceNumber,
          invoiceDate: moment().format('YYYY-MM-DD'),
          dueDate: moment().add(3, 'days').format('YYYY-MM-DD'),
          recipientEntityId: sponsor?.financeDetail?.entityId,
          issuerEntityId: selectedSite?.siteEntityId,
          amount: totalAmountDue,
          clinicalStudyId: trialId,
          studySiteId: trialSiteId,
          items: invoiceItems.map((i) => {
            return {
              description: i.description,
              amount: Number(i.amount),
              quantity: Number(i.quantity),
            };
          }),
        };

        const res = await generateFianceInvoice({ body, actor });
        if (res.data) {
          handleClose();
        } else if (res.error) {
          dispatch(
            openMessage({
              messageSeverity: 'error',
              message: getApiErrorMessage(res.error, 'Failed to create invoice.'),
            })
          );
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to generate invoice: ${error.message || 'Unknown error'}`,
        })
      );
    }
  };

  const isInvoiceValid = useMemo(() => {
    return invoiceItems.every(
      (item) =>
        item.description && item.description.trim() !== '' && Number(item.amount) > 0 && Number(item.quantity) > 0
    );
  }, [invoiceItems]);

  // Cancel edit mode if user clicks outside
  const handleCancelEdit = () => {
    if (editIndex !== null) {
      setEditIndex(null);
    }
  };

  return (
    <Card elevation={10}>
      <CardHeader
        title="Invoice"
        titleTypographyProps={{ variant: 'h4', fontWeight: 600 }}
        sx={{ fontWeight: 600, textAlign: 'center' }}
      />
      <CardContent sx={{ px: 5 }} onClick={handleCancelEdit}>
        <Grid container spacing={3}>
          {!isUpdate && pendingAmendments.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                {pendingAmendments.length} pending amendment
                {pendingAmendments.length > 1 ? 's' : ''} will be attached to this invoice
                automatically. Each amendment and its line-item breakdown are shown in the services
                table below.
              </Alert>
            </Grid>
          )}
          {isUpdate && isTerminalInvoice && appliedAmendments.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                This invoice includes {appliedAmendments.length} adjustment
                {appliedAmendments.length > 1 ? 's' : ''} from previous invoice amendment
                {appliedAmendments.length > 1 ? 's' : ''}, with a full line-item breakdown in the
                services table.
              </Alert>
            </Grid>
          )}
          {isUpdate && isTerminalInvoice && recordedAmendments.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This invoice has {recordedAmendments.length} amendment record
                {recordedAmendments.length > 1 ? 's' : ''}. The adjustment
                {recordedAmendments.length > 1 ? 's are' : ' is'} scheduled for a future invoice
                and {recordedAmendments.length > 1 ? 'do' : 'does'} not change the approved amount
                below. See the item breakdown in the amendment records section.
              </Alert>
            </Grid>
          )}
          {isUpdate && isApprovedOrAmended && isEditable && (
            <Grid item xs={12}>
              <Alert severity="info">
                Changes to an approved invoice create an amendment record applied to the next invoice. The
                current invoice amount and payment are unchanged.
              </Alert>
            </Grid>
          )}
          {isUpdate && isPendingInvoice && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Updating a pending invoice does not re-notify the sponsor. Amendment type and reason use
                defaults unless this invoice was already approved.
              </Alert>
            </Grid>
          )}
          {isUpdate && currentInvoiceStatus === 'DECLINED' && (
            <Grid item xs={12}>
              <Alert severity="error">Declined invoices cannot be updated. Create a new invoice instead.</Alert>
            </Grid>
          )}
          <Grid item xs={5} rowGap={2}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Issuer Information
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Name: &nbsp; <span style={{ fontWeight: 500 }}>{siteInfo?.name || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Address:&nbsp;{' '}
              <span style={{ fontWeight: 500 }}>
                {siteInfo?.address
                  ? `${siteInfo?.address?.line1 || ''}, ${siteInfo?.address?.line2 || ''}, ${
                      siteInfo?.address?.city || ''
                    }`
                  : 'N/A'}
              </span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Phone: &nbsp;<span style={{ fontWeight: 500 }}>{siteInfo?.contact || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Email: &nbsp;<span style={{ fontWeight: 500 }}>{siteInfo?.email || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ pt: 0 }}></Grid>
          <Grid item xs={5}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Recipient Information
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Name: &nbsp; <span style={{ fontWeight: 500 }}>{sponsor?.orgnizationName || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Address:&nbsp; <span style={{ fontWeight: 500 }}>{sponsor?.address || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Phone: &nbsp;<span style={{ fontWeight: 500 }}>{sponsor?.contactNumber || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Email: &nbsp;<span style={{ fontWeight: 500 }}>{sponsor?.contactEmail || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={7} sx={{ pt: 0 }}></Grid>
          <Grid item xs={4}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Invoice Details
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Invoice Number:&nbsp; <span style={{ fontWeight: 500 }}>{invoiceNumber || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Date:&nbsp; <span style={{ fontWeight: 500 }}>{invoiceDate || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600, mb: 1 }}>
              Description of Services
            </Typography>
            <TableContainer>
              <Table aria-label="spanning table">
                <CustomTableHead>
                  <TableRow>
                    {columns &&
                      columns.map((col) => (
                        <CustomTableHeadCell
                          key={col.key}
                          align={col.align}
                          sx={{
                            minWidth: col?.minWidth,
                            maxWidth: col?.maxWidth,
                          }}
                        >
                          {col.label}
                        </CustomTableHeadCell>
                      ))}
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {invoiceItems.length > 0 ? (
                    invoiceItems.map((row, index) => (
                      <TableRow key={index} onClick={(e) => e.stopPropagation()}>
                        {columns.map((column) => {
                          if (column.key === 'actions' && isEditable) {
                            return (
                              <TableCell align="center" key={column.key} sx={{ py: 1 }}>
                                {editIndex === index ? (
                                  <IconButton
                                    color="success"
                                    onClick={handleSave}
                                    disabled={isSubmitting || !formData.description || formData.description.trim() === ''}
                                  >
                                    <Check />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleEdit(index)}
                                    disabled={isSubmitting || editIndex !== null}
                                  >
                                    <Edit />
                                  </IconButton>
                                )}
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveRow(index)}
                                  disabled={invoiceItems.length === 1 || isSubmitting || editIndex !== null}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            );
                          }
                          if (column.key === 'srno') {
                            return (
                              <TableCell align="center" key={column.key} sx={{ py: 1 }}>
                                {index + 1}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.key} align={column.align} sx={{ py: 1 }}>
                              {editIndex === index && isEditable ? (
                                <TextField
                                  variant="standard"
                                  value={formData[column.key]}
                                  onChange={(e) => handleChange(e, column.key)}
                                  size="small"
                                  error={
                                    column.key === 'description' &&
                                    (!formData.description || formData.description.trim() === '')
                                  }
                                  helperText={
                                    column.key === 'description' &&
                                    (!formData.description || formData.description.trim() === '')
                                      ? 'Required'
                                      : ''
                                  }
                                  type={column.key === 'amount' || column.key === 'quantity' ? 'number' : 'text'}
                                  InputProps={{
                                    readOnly: column.key === 'total',
                                    inputProps: {
                                      min: column.key === 'amount' || column.key === 'quantity' ? 0 : undefined,
                                      step: column.key === 'amount' ? 0.01 : column.key === 'quantity' ? 1 : undefined,
                                    },
                                  }}
                                  fullWidth
                                />
                              ) : (
                                <>
                                  {column.key === 'amount' || column.key === 'total' ? '$' : ''}
                                  {column.key === 'amount' || column.key === 'total'
                                    ? Number(row[column.key]).toFixed(2)
                                    : row[column.key]}
                                </>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        No items found. {isEditable && 'Click Add Row to add an item.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {!isUpdate &&
                    renderAmendmentTableRows(pendingAmendmentRows, { includeActions: isEditable })}
                  {isUpdate &&
                    renderAmendmentTableRows(appliedAmendmentRows, { includeActions: isEditable })}
                  {isEditable && (
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }} onClick={(e) => e.stopPropagation()}>
                      <TableCell colSpan={columns.length - 1} align="center" sx={{ py: 1 }}></TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleAddRow}
                          disabled={isSubmitting || editIndex !== null}
                        >
                          Add Row
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {isUpdate && isTerminalInvoice && recordedAmendmentRows.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" color="initial" sx={{ fontWeight: 600, mb: 1 }}>
                Amendment Records on This Invoice
              </Typography>
              <TableContainer>
                <Table aria-label="amendment records table" size="small">
                  <CustomTableHead>
                    <TableRow>
                      {columns
                        .filter((col) => col.key !== 'actions')
                        .map((col) => (
                          <CustomTableHeadCell
                            key={col.key}
                            align={col.align}
                            sx={{ minWidth: col?.minWidth, maxWidth: col?.maxWidth }}
                          >
                            {col.label}
                          </CustomTableHeadCell>
                        ))}
                      <CustomTableHeadCell align="center">Status</CustomTableHeadCell>
                    </TableRow>
                  </CustomTableHead>
                  <TableBody>
                    {renderAmendmentTableRows(recordedAmendmentRows, {
                      includeActions: false,
                      showStatusColumn: true,
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
          <Box sx={{ height: 15, width: '100%' }}></Box>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box>
              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Subtotal:
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {subtotal.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Taxes (10%):
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {taxes.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Stripe Charges (ACH 0.8%):
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {stripeCharges.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Platform fee (0.05%):
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="initial"
                  sx={{ fontWeight: 600, width: '30%', height: '100%', display: 'flex', alignItems: 'flex-end' }}
                >
                  <span style={{ fontWeight: 500 }}>$ {transactionHandlingCost.toFixed(2)}</span>
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Total Amount Due:
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}> {`$ ${totalAmountDue.toFixed(2)}`}</span>
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {isUpdate && isTerminalInvoice && (
            <ReadonlyApprovalDetails
              invoiceDetails={invoiceData?.invoiceDetails}
              invoiceId={invoiceId}
            />
          )}
          <Box sx={{ height: 50, width: '100%' }}></Box>

          {
            <Formik
              initialValues={{
                amendmentType: '',
                reason: '',
              }}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={async (values, { setSubmitting }) => {
                try {
                } catch (error) {
                  console.error('API Error:', error);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, validateForm, setSubmitting, setFieldValue }) => {
                return (
                  <>
                    {isUpdate && isApprovedOrAmended && (
                        <>
                          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
                            <FormControl
                              fullWidth
                              variant="standard"
                              error={touched.amendmentType && Boolean(errors.amendmentType)}
                              //  sx={{ mb: 2 }}
                            >
                              <Select
                                name="amendmentType"
                                value={values.amendmentType}
                                onChange={handleChange}
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  Select amendment type
                                </MenuItem>
                                {[
                                  { label: 'Correction', value: 'correction' },
                                  { label: 'Additional Charge', value: 'additional_charge' },
                                  { label: 'Refund', value: 'refund' },
                                  { label: 'Price Adjustment', value: 'price_adjustment' },
                                  // { label: 'Budget Revision', value: 'Budget Revision' },
                                  // { label: 'Rate Change', value: 'Rate Change' },
                                  // { label: 'Overhead/Indirect Cost Update', value: 'Overhead/Indirect Cost Update' },
                                  // { label: 'Site Addition/Removal', value: 'Site Addition/Removal' },
                                  // { label: 'Patient Count Adjustment', value: 'Patient Count Adjustment' },
                                  // { label: 'Protocol Change Impact', value: 'Protocol Change Impact' },
                                  // { label: 'Data Entry Correction', value: 'Data Entry Correction' },
                                  // { label: 'Billing Error Correction', value: 'Billing Error Correction' },
                                  // { label: 'Tax or Currency Adjustment', value: 'Tax or Currency Adjustment' },
                                  // { label: 'Payment Schedule Change', value: 'Payment Schedule Change' },
                                  // { label: 'Milestone Date Adjustment', value: 'Milestone Date Adjustment' },
                                  // { label: 'Charge Dispute Adjustment', value: 'Charge Dispute Adjustment' },
                                  // { label: 'Credit Note/Refund', value: 'Credit Note/Refund' },
                                  // { label: 'Contract Renegotiation', value: 'Contract Renegotiation' },
                                  // { label: 'New Service Addition', value: 'New Service Addition' },
                                  // { label: 'Pass-Through Cost Inclusion', value: 'Pass-Through Cost Inclusion' },
                                  // { label: 'Billing Detail Update', value: 'Billing Detail Update' },
                                  // { label: 'Invoice Splitting/Merging', value: 'Invoice Splitting/Merging' },
                                  // { label: 'Regulatory Fee Adjustment', value: 'Regulatory Fee Adjustment' },
                                  // { label: 'Compliance Charge Addition', value: 'Compliance Charge Addition' },
                                ].map((type) => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {touched.amendmentType && errors.amendmentType && (
                                <FormHelperText>{errors.amendmentType}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={4} sx={{}}></Grid>
                          <Grid item xs={4} sx={{}}>
                            <TextField
                              size="medium"
                              label="Reason"
                              variant="standard"
                              name="reason"
                              fullWidth
                              multiline
                              maxRows={3}
                              value={values.reason}
                              onChange={handleChange}
                              error={touched.reason && Boolean(errors.reason)}
                              helperText={touched.reason && errors.reason}
                            />
                          </Grid>
                        </>
                      )}
                    <Box sx={{ height: 20, width: '100%' }}></Box>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CustomButton
                        variant="outlined"
                        size="small"
                        sx={{ mr: 2 }}
                        disabled={isSubmitting}
                        onClick={handleClose}
                      >
                        Cancel
                      </CustomButton>
                      {isEditable && (
                        <CustomButton
                          variant="contained"
                          size="small"
                          disabled={
                            isSubmitting || !isInvoiceValid || editIndex !== null || invoiceItems.length === 0
                          }
                          onClick={() => handleGenerateInvoice(values, validateForm)}
                        >
                          {isUpdate
                            ? isApprovedOrAmended
                              ? 'Record Amendment'
                              : 'Update Invoice'
                            : 'Create Invoice'}
                        </CustomButton>
                      )}
                    </Grid>
                  </>
                );
              }}
            </Formik>
          }
        </Grid>
      </CardContent>
    </Card>
  );
};

export default GenerateInvoice;
